import ApiService from "../../services/ApiService";
import translate from "../../services/Translate";

export type StudentCardRejectionReason = "invalid_image" | "invalid_birthdate" | "other";

export type StudentCardRegenerationPreview = {
  user_id: number;
  full_name: string;
  email: string;
  birthdate: string;
  image_link: string;
  language: string;
  requested_at?: string | null;
};

export type StudentCardRegenerateExecutePageState =
  | { status: "loading" }
  | { status: "ready"; preview: StudentCardRegenerationPreview; token: string }
  | { status: "error"; title: string; message: string };

const API_ERROR_TRANSLATIONS: Record<string, { titleKey: string; messageKey: string }> = {
  "Missing regeneration token.": {
    titleKey: "PAGES.STUDENT_CARD_REGENERATE_EXECUTE.ERROR.INVALID_TOKEN.TITLE",
    messageKey: "PAGES.STUDENT_CARD_REGENERATE_EXECUTE.ERROR.INVALID_TOKEN.MESSAGE",
  },
  "Invalid regeneration link.": {
    titleKey: "PAGES.STUDENT_CARD_REGENERATE_EXECUTE.ERROR.INVALID_TOKEN.TITLE",
    messageKey: "PAGES.STUDENT_CARD_REGENERATE_EXECUTE.ERROR.INVALID_TOKEN.MESSAGE",
  },
  "Bad Signature.": {
    titleKey: "PAGES.STUDENT_CARD_REGENERATE_EXECUTE.ERROR.INVALID_TOKEN.TITLE",
    messageKey: "PAGES.STUDENT_CARD_REGENERATE_EXECUTE.ERROR.INVALID_TOKEN.MESSAGE",
  },
  "This regeneration link has expired.": {
    titleKey: "PAGES.STUDENT_CARD_REGENERATE_EXECUTE.ERROR.EXPIRED.TITLE",
    messageKey: "PAGES.STUDENT_CARD_REGENERATE_EXECUTE.ERROR.EXPIRED.MESSAGE",
  },
  "Signature Expired.": {
    titleKey: "PAGES.STUDENT_CARD_REGENERATE_EXECUTE.ERROR.EXPIRED.TITLE",
    messageKey: "PAGES.STUDENT_CARD_REGENERATE_EXECUTE.ERROR.EXPIRED.MESSAGE",
  },
  "This regeneration request has been rejected.": {
    titleKey: "PAGES.STUDENT_CARD_REGENERATE_EXECUTE.ERROR.REJECTED.TITLE",
    messageKey: "PAGES.STUDENT_CARD_REGENERATE_EXECUTE.ERROR.REJECTED.MESSAGE",
  },
  "At least one rejection reason is required.": {
    titleKey: "PAGES.STUDENT_CARD_REGENERATE_EXECUTE.REJECT.ERROR.TITLE",
    messageKey: "PAGES.STUDENT_CARD_REGENERATE_EXECUTE.REJECT.ERROR.NO_REASON",
  },
  "Other rejection reason is required when 'other' is selected.": {
    titleKey: "PAGES.STUDENT_CARD_REGENERATE_EXECUTE.REJECT.ERROR.TITLE",
    messageKey: "PAGES.STUDENT_CARD_REGENERATE_EXECUTE.REJECT.ERROR.OTHER_REQUIRED",
  },
  "No pending student card regeneration request found.": {
    titleKey: "PAGES.STUDENT_CARD_REGENERATE_EXECUTE.ERROR.GENERIC.TITLE",
    messageKey: "PAGES.STUDENT_CARD_REGENERATE_EXECUTE.ERROR.GENERIC.MESSAGE",
  },
  "Student card regeneration request is incomplete.": {
    titleKey: "PAGES.STUDENT_CARD_REGENERATE_EXECUTE.ERROR.GENERIC.TITLE",
    messageKey: "PAGES.STUDENT_CARD_REGENERATE_EXECUTE.ERROR.GENERIC.MESSAGE",
  },
};

export const getStudentLanguageLabel = (language: string, studentLanguage: string): string =>
  studentLanguage.startsWith("FR")
    ? translate(language, "PAGES.STUDENT_CARD_REGENERATE_EXECUTE.REJECT.STUDENT_LANGUAGE_FRENCH")
    : translate(language, "PAGES.STUDENT_CARD_REGENERATE_EXECUTE.REJECT.STUDENT_LANGUAGE_ENGLISH");

export const translateStudentCardRegenerateExecuteError = (
  language: string,
  apiMessage?: string
): { title: string; message: string } => {
  const fallback = {
    title: translate(language, "PAGES.STUDENT_CARD_REGENERATE_EXECUTE.ERROR.GENERIC.TITLE"),
    message: translate(language, "PAGES.STUDENT_CARD_REGENERATE_EXECUTE.ERROR.GENERIC.MESSAGE"),
  };

  if (!apiMessage) {
    return fallback;
  }

  const translation = API_ERROR_TRANSLATIONS[apiMessage];
  if (translation) {
    return {
      title: translate(language, translation.titleKey),
      message: translate(language, translation.messageKey),
    };
  }

  return {
    title: fallback.title,
    message: apiMessage,
  };
};

export const fetchStudentCardRegenerationPreview = (token: string): Promise<StudentCardRegenerationPreview> => {
  return ApiService.get(`student-cards/regenerate/preview?token=${encodeURIComponent(token)}`).then(
    (response) => response.data.regeneration_request
  );
};

export const executeStudentCardRegeneration = (token: string): Promise<string> => {
  return ApiService.post("student-cards/regenerate/confirm", { token }).then(
    (response) => response.data.student_card_url
  );
};

export const rejectStudentCardRegeneration = (
  token: string,
  reasons: StudentCardRejectionReason[],
  otherReason?: string
): Promise<void> => {
  return ApiService.post("student-cards/regenerate/reject", {
    token,
    reasons,
    other_reason: otherReason,
  }).then(() => undefined);
};
